import React, { useState, useEffect } from 'react';
import './Academia.css';
import Footer from '../../components/Footer/Footer';
import AcademiaCarousel from '../../components/AcademiaCarousel/AcademiaCarousel';
import CardImageCarousel from '../../components/CardImageCarousel/CardImageCarousel';
import { fetchAcademiaItems } from '../../services/api';

const Academia = () => {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [itemsData, setItemsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage') || 'KU';
    setCurrentLang(savedLang);

    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
      localStorage.setItem('selectedLanguage', event.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // Fetch items from API
  useEffect(() => {
    const loadItems = async () => {
      try {
        setLoading(true);
        const items = await fetchAcademiaItems();
        setItemsData(items);
      } catch (error) {
        console.error('Failed to load academia items:', error);
        setItemsData([]);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };
    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const content = {
    EN: {
      title: 'Academia',
      subtitle: 'Explore our academic programs, courses, and educational resources designed to enhance your knowledge and skills.'
    },
    KU: {
      title: 'ئەکادیمیا',
      subtitle: 'بەرنامە، کۆرس و سەرچاوە پەروەردەییەکانمان بزانە کە دروستکراون بۆ باشترکردنی زانست و لێهاتووییەکانت.'
    },
    AR: {
      title: 'الأكاديمية',
      subtitle: 'استكشف برامجنا الأكاديمية ودوراتنا ومواردنا التعليمية المصممة لتعزيز معرفتك ومهاراتك.'
    },
    TR: {
      title: 'Akademi',
      subtitle: 'Bilginizi ve becerilerinizi geliştirmek için tasarlanmış akademik programlarımızı, kurslarımızı ve eğitim kaynaklarımızı keşfedin.'
    }
  };

  const pageContent = content[currentLang] || content.KU;

  return (
    <div className="academia-page">
      <div className="container">
        <div className="academia-content">
          {/* Title Section */}
          <div className="academia-title-section">
            <h1 className="page-title">{pageContent.title}</h1>
            <p className="page-subtitle">{pageContent.subtitle}</p>
          </div>

          {/* Carousel Section */}
          <div className="academia-carousel-section">
            <AcademiaCarousel />
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading items...</p>
            </div>
          )}

          {/* Items Grid */}
          {!loading && (
            <div className="academia-items-grid">
              {itemsData.map((item) => {
                // Transform API data to match component structure
                const itemContent = {
                  title: item.title?.[currentLang] || item.title?.EN || '',
                  description: item.description?.[currentLang] || item.description?.EN || ''
                };
                
                // Transform image URLs from API - ensure we get all images
                const images = (item.images || [])
                  .filter(img => img !== null && img !== undefined)
                  .map(img => {
                    if (typeof img === 'string') {
                      return img;
                    }
                    return img.url || img.src || img;
                  })
                  .filter(url => url && url.trim() !== '');

                return (
                  <div key={item.id} className="academia-item-card">
                    {/* Item Images with Carousel */}
                    {images && images.length > 0 && (
                      <div className="academia-item-images-container">
                        <CardImageCarousel 
                          images={images} 
                          altText={itemContent.title}
                        />
                      </div>
                    )}

                    {/* Item Content */}
                    <div className="academia-item-content">
                      <h2 className="academia-item-title">{itemContent.title}</h2>
                      <p className="academia-item-description">{itemContent.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {!loading && itemsData.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No items found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="image-modal-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close"
            >
              ×
            </button>
            <img src={selectedImage} alt="Enlarged view" />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Academia;

